/**
 * SESONGVISNINGEN I PROGRAMMERINGEN (QA13 25, 253.75–253.76).
 *
 * Festivalen programmerer en årgang (scene × dag). Et spillested programmerer saler og datoer
 * gjennom en sesong, og sesongen er kalenderåret: årgangen finnes i bakgrunnen
 * (`app.ensure_venue_edition`) og vises aldri som ord. Radene er bookingsakene (0927) huset eier i
 * spillestedets saler det året, én per sal og dato — samme sak som Bud-fanen og Forespørsler leser.
 */
import { z } from 'zod';

import { engagementRefDtoSchema } from './engagements.js';

export const bookingSeasonQuerySchema = z
  .object({
    festivalId: z.uuid(),
    year: z.coerce.number().int().min(1900).max(2200),
  })
  .meta({ id: 'BookingSeasonQuery' });
export type BookingSeasonQuery = z.infer<typeof bookingSeasonQuerySchema>;

export const bookingSeasonRoomDtoSchema = z
  .object({ id: z.uuid(), name: z.string() })
  .meta({ id: 'BookingSeasonRoom' });

export const bookingSeasonItemDtoSchema = z
  .object({
    engagementId: z.uuid(),
    bookingArtistId: z.uuid().nullable(),
    artistName: z.string(),
    /** `YYYY-MM-DD`. */
    date: z.string(),
    roomId: z.uuid().nullable(),
    roomName: z.string().nullable(),
    engagement: engagementRefDtoSchema,
    /** Beløpet på budet saken står på (det godtatte, ellers det nyeste). Null uten «Se priser». */
    amountMinor: z.number().int().nullable(),
    currency: z.string().length(3).nullable(),
  })
  .meta({ id: 'BookingSeasonItem' });
export type BookingSeasonItemDto = z.infer<typeof bookingSeasonItemDtoSchema>;

export const bookingSeasonResponseSchema = z
  .object({
    festivalId: z.uuid(),
    year: z.number().int(),
    rooms: z.array(bookingSeasonRoomDtoSchema),
    items: z.array(bookingSeasonItemDtoSchema),
    /**
     * Tallene, bare for den som ser beløp (ellers null): budsjettet for sesongen, det som er
     * programmert (alle saker som ikke har falt bort eller er avlyst, som brettets «Koster») og det
     * som er avtalt (Muntlig bekreftet og sterkere). Summene er i budsjettets valuta.
     */
    budgetMinor: z.number().int().nullable(),
    programmedMinor: z.number().int().nullable(),
    agreedMinor: z.number().int().nullable(),
    currency: z.string().length(3),
  })
  .meta({ id: 'BookingSeasonResponse' });
export type BookingSeasonResponse = z.infer<typeof bookingSeasonResponseSchema>;

/** `PUT /v1/booking/season/budget` — null tar budsjettet bort. */
export const setSeasonBudgetRequestSchema = z
  .object({
    festivalId: z.uuid(),
    year: z.number().int().min(1900).max(2200),
    budgetMinor: z.number().int().nonnegative().max(100_000_000_000).nullable(),
    currency: z.string().length(3).default('NOK'),
  })
  .meta({ id: 'SetSeasonBudgetRequest' });
export type SetSeasonBudgetRequest = z.infer<typeof setSeasonBudgetRequestSchema>;
